import { isComplete, isEdited, isEmpty, needsReview, sectionLeaves, type FaqQuestion, type Leaf, type PortalConfig, type SectionDocs, type SectionId } from '@sonder/shared'
import { orderBy } from 'firebase/firestore'
import { useMemo } from 'react'
import { enabledSections } from '@/data/client'
import { useCollection } from '@/data/hooks'
import type { PillTone } from '@/ui'

export type ReviewRow = Leaf & { section: SectionId; order: number }

/** One answer's flag. `team` adds what only staff care about: who last edited it, and what is the client's to do. */
export function answerFlag(r: Pick<Leaf, 'field' | 'required' | 'confirmable' | 'byClient'>, team: boolean): { tone: PillTone; label: string } | null {
  const f = r.field
  // Granting access is the client's to do, so for the team it is waiting, not missing.
  if (team && r.byClient && (!f || isEmpty(f.value) || f.value === 'not_started')) return { tone: 'neutral', label: 'Client’s step' }
  if (!f || isEmpty(f.value)) return r.required ? { tone: 'danger', label: 'Missing' } : null
  if (team && f.source === 'client') return { tone: 'ink', label: 'Edited by client' }
  if (isEdited(f)) return { tone: 'ok', label: 'Edited' }
  if (r.confirmable && needsReview(f)) return { tone: 'warn', label: 'To confirm' }
  if (!r.confirmable && f.prefilled && !f.confirmed) return { tone: 'neutral', label: 'Prefilled' }
  if (isComplete(f)) return { tone: 'ok', label: 'Confirmed' }
  return { tone: 'neutral', label: 'Prefilled' }
}

/**
 * Every answer in the onboarding, per enabled step, in page order. FAQ answers take the question's wording and
 * order from the shared catalog (and this client's own wording), and questions no longer asked are left out.
 */
export function useReviewRows(config: PortalConfig | null, docs: Partial<SectionDocs>, brandCount?: number): ReviewRow[] {
  const catalog = useCollection<Omit<FaqQuestion, 'id'>>('faqQuestions', [orderBy('order')])
  return useMemo(() => {
    if (!config) return []
    const faq = docs.faq
    const standard = new Map(catalog.items.map((q) => [q.id, q]))
    const asked = (qid: string) => {
      if (faq?.custom?.[qid]) return true
      const q = standard.get(qid)
      if (q && !q.active) return false
      return !config.faqQuestionIds || config.faqQuestionIds.includes(qid)
    }
    return enabledSections(config, brandCount).flatMap((s) => {
      const id = s.id as SectionId
      const leaves = sectionLeaves(id, config, docs).map((l, i) => ({ ...l, section: id, order: i }))
      if (id !== 'faq') return leaves
      return leaves
        .filter((l) => !l.questionId || asked(l.questionId))
        .map((l) => {
          const qid = l.questionId
          if (!qid) return l
          const custom = faq?.custom?.[qid]
          const label = custom?.label ?? faq?.labels?.[qid] ?? standard.get(qid)?.label ?? l.label
          return { ...l, label, order: custom?.order ?? standard.get(qid)?.order ?? l.order }
        })
    })
  }, [config, docs, brandCount, catalog.items])
}

export function reviewAnchor(id: string) {
  return `review-${id}`
}

